import { useMemo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { isQuinnChartType } from "../../types";
import { QuinnKpi, QuinnKpiBlock } from "../blocks/QuinnKpiBlock.tsx";
import { QuinnChartBlock, QuinnChartSpec } from "../blocks/QuinnChartBlock.tsx";

function parseBlock<T>(raw: string): T | null {
    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

function extractLanguage(className?: string): string | null {
    if (!className) return null;
    const match = className.split(" ").find((entry) => entry.startsWith("language-"));
    return match ? match.replace("language-", "") : null;
}

export function QuinnMarkdown({ content }: { content: string }) {
    const components = useMemo(
        () => ({
            p: ({ children }: any) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
            strong: ({ children }: any) => <strong className="font-semibold text-gray-900">{children}</strong>,
            em: ({ children }: any) => <em className="italic">{children}</em>,
            a: ({ children, href }: any) => (
                <a
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
                >
                    {children}
                </a>
            ),
            h1: ({ children }: any) => <h1 className="mb-2 mt-3 text-base font-semibold text-gray-900">{children}</h1>,
            h2: ({ children }: any) => <h2 className="mb-2 mt-3 text-sm font-semibold text-gray-900">{children}</h2>,
            h3: ({ children }: any) => <h3 className="mb-1 mt-3 text-sm font-semibold text-gray-800">{children}</h3>,
            ul: ({ children }: any) => <ul className="mb-2 list-disc space-y-1 pl-5 marker:text-gray-400">{children}</ul>,
            ol: ({ children }: any) => <ol className="mb-2 list-decimal space-y-1 pl-5 marker:text-gray-400">{children}</ol>,
            li: ({ children }: any) => <li className="leading-relaxed">{children}</li>,
            blockquote: ({ children }: any) => (
                <blockquote className="my-2 border-l-2 border-primary/40 bg-primary/5 py-1 pl-3 text-gray-700">{children}</blockquote>
            ),
            hr: () => <hr className="my-3 border-gray-200" />,
            table: ({ children }: any) => (
                <div className="my-3 overflow-x-auto rounded-xl border border-gray-200">
                    <table className="w-full border-collapse text-xs">{children}</table>
                </div>
            ),
            thead: ({ children }: any) => <thead className="bg-gray-50">{children}</thead>,
            th: ({ children, style }: any) => (
                <th
                    style={style}
                    className="whitespace-nowrap border-b border-gray-200 px-2.5 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-gray-500"
                >
                    {children}
                </th>
            ),
            td: ({ children, style }: any) => (
                <td style={style} className="border-b border-gray-100 px-2.5 py-1.5 tabular-nums text-gray-800 last:border-r-0">
                    {children}
                </td>
            ),
            tr: ({ children }: any) => <tr className="last:[&>td]:border-b-0 hover:bg-gray-50/60">{children}</tr>,
            pre: ({ children }: any) => <>{children}</>,
            code: ({ className, children }: any) => {
                const language = extractLanguage(className);
                const raw = String(children ?? "").trim();

                if (language === "quinn:kpi") {
                    const parsed = parseBlock<QuinnKpi[]>(raw);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        return <QuinnKpiBlock items={parsed} />;
                    }
                }

                if (language === "quinn:chart") {
                    const parsed = parseBlock<QuinnChartSpec>(raw);
                    if (parsed && isQuinnChartType(parsed.chart_type) && parsed.points?.length > 0) {
                        return <QuinnChartBlock spec={parsed} />;
                    }
                }

                if (!className) {
                    return <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] text-gray-800">{children}</code>;
                }

                return (
                    <pre className="my-2 overflow-x-auto rounded-xl border border-gray-200 bg-gray-50 p-3">
                        <code className="font-mono text-[11px] leading-relaxed text-gray-800">{children}</code>
                    </pre>
                );
            },
        }),
        [],
    );

    return (
        <div className="text-sm text-gray-800">
            <Markdown remarkPlugins={[remarkGfm]} components={components as any}>
                {content}
            </Markdown>
        </div>
    );
}
